import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const LINKS = [
  { label: "What We Do", href: "#what-we-do" },
  { label: "Solutions", href: "#solutions" },
  { label: "Work", href: "#work" },
  { label: "Products", href: "#products" },
  { label: "Industries", href: "#industries" },
  { label: "About", href: "#about" },
  { label: "Insights", href: "#insights" },
];

export const Navbar = ({ onContact }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "jx-glass shadow-[0_8px_30px_rgb(0,0,0,0.05)]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-[76px] flex items-center justify-between">
        <a href="#top" onClick={(e) => go(e, "#top")} data-testid="nav-logo" className="flex items-center">
          <img src="/assets/logo-horizontal-sm.png" alt="JIYONOX" className="h-9 w-auto" width="282" height="36" />
        </a>
        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-jx-ink/70 hover:text-jx-ink transition-colors duration-300 relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 jx-gradient-bg transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-button"
            onClick={onContact}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-jx-ink text-white text-sm font-medium px-5 py-2.5 transition-[transform,background-color] duration-300 hover:scale-[1.04] hover:bg-jx-purple active:scale-95"
          >
            Start a Conversation
            <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-jx-ink"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden jx-glass border-t border-black/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a key={l.label} href={l.href} onClick={(e) => go(e, l.href)} data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="font-display font-medium text-lg text-jx-ink">
                  {l.label}
                </a>
              ))}
              <button
                data-testid="nav-mobile-cta-button"
                onClick={() => { setOpen(false); onContact(); }}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-jx-ink text-white font-medium px-5 py-3"
              >
                Start a Conversation <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
